import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { demoDataGenerator } from '../../utils/demoDataGenerator';

const PreviewCalculators = () => {
  const navigate = useNavigate();
  const [activeCalculator, setActiveCalculator] = useState('chemical');
  
  const demoData = useMemo(() => demoDataGenerator?.getCalculatorExamples(), []);

  const calculators = [
    { id: 'chemical', name: 'Chemical Calculator', icon: 'Calculator', tier: 'basic' },
    { id: 'beneficial', name: 'Beneficial Calculator', icon: 'Bug', tier: 'premium' },
    { id: 'tank-mixing', name: 'Tank Mixing', icon: 'FlaskConical', tier: 'premium' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Preview Banner */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Icon name="Eye" size={24} />
              <div>
                <h3 className="font-semibold text-lg">Preview Mode - Premium Calculators</h3>
                <p className="text-sm text-green-100">Sign up to access all calculators and save your calculations</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="bg-white text-green-600 hover:bg-green-50 border-white"
                onClick={() => navigate('/pricing-page')}
              >
                View Plans
              </Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white"
                onClick={() => navigate('/login')}
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Premium Calculator Suite</h1>
          <p className="text-gray-600 mt-2">Advanced IPM calculation tools for precise application rates</p>
        </div>

        {/* Calculator Tabs */}
        <div className="mb-6 flex gap-2 overflow-x-auto">
          {calculators?.map(calc => (
            <button
              key={calc?.id}
              onClick={() => setActiveCalculator(calc?.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium whitespace-nowrap transition-colors ${
                activeCalculator === calc?.id
                  ? 'bg-green-600 text-white' :'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              <Icon name={calc?.icon} size={20} />
              {calc?.name}
              {calc?.tier === 'premium' && (
                <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs rounded-full font-semibold">
                  PRO
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Chemical Calculator */}
        {activeCalculator === 'chemical' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Chemical Calculator</h2>
              <p className="text-gray-600">Calculate precise mixing ratios and application rates</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                  <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-900">{demoData?.chemical?.productName}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Concentration (%)</label>
                  <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-900">{demoData?.chemical?.concentration}%</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Application Rate</label>
                  <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-900">{demoData?.chemical?.targetRate} oz/gallon</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Area Size</label>
                  <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-900">{demoData?.chemical?.areaSize} {demoData?.chemical?.unit}</div>
                </div>
              </div>

              {/* Results Section */}
              <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-6 border-2 border-green-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Calculation Results</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-600">Product Needed:</span>
                    <span className="text-xl font-bold text-green-600">{demoData?.chemical?.result?.productNeeded} oz</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-600">Water Needed:</span>
                    <span className="text-xl font-bold text-blue-600">{demoData?.chemical?.result?.waterNeeded} gallons</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-600">Mixing Ratio:</span>
                    <span className="text-xl font-bold text-purple-600">{demoData?.chemical?.result?.mixingRatio}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-600">Total Volume:</span>
                    <span className="text-xl font-bold text-gray-900">{demoData?.chemical?.result?.applicationVolume} gallons</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Beneficial Calculator */}
        {activeCalculator === 'beneficial' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Beneficial Calculator</h2>
                <p className="text-gray-600">Calculate optimal release rates for biological controls</p>
              </div>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">PREMIUM</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Pest</label>
                  <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-900">{demoData?.beneficial?.pest}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Beneficial Insect</label>
                  <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-900">{demoData?.beneficial?.beneficial}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pest Pressure</label>
                  <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-900 capitalize">{demoData?.beneficial?.pestPressure}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Area Size</label>
                  <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-900">{demoData?.beneficial?.areaSize} sq ft</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Crop Type</label>
                  <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-900">{demoData?.beneficial?.cropType}</div>
                </div>
              </div>

              {/* Results Section */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 border-2 border-purple-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Release Recommendations</h3>
                <div className="space-y-4">
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Release Rate</div>
                    <div className="text-2xl font-bold text-purple-600">{demoData?.beneficial?.result?.releaseRate} per sq ft</div>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Total Beneficials Needed</div>
                    <div className="text-2xl font-bold text-green-600">{demoData?.beneficial?.result?.totalBeneficials?.toLocaleString()}</div>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Packages Required</div>
                    <div className="text-2xl font-bold text-blue-600">{demoData?.beneficial?.result?.packagesNeeded} packages</div>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Release Schedule</div>
                    <div className="text-lg font-semibold text-gray-900">{demoData?.beneficial?.result?.releaseSchedule}</div>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Estimated Cost</div>
                    <div className="text-2xl font-bold text-orange-600">${demoData?.beneficial?.result?.estimatedCost?.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tank Mixing Calculator */}
        {activeCalculator === 'tank-mixing' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Tank Mixing Procedures</h2>
                <p className="text-gray-600">Professional tank mixing with compatibility checks</p>
              </div>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">PREMIUM</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Products Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Products to Mix</h3>
                {demoData?.tankMixing?.products?.map((product, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{product?.name}</div>
                        <div className="text-sm text-gray-600">Amount: {product?.amount} {product?.unit}</div>
                      </div>
                      <Icon name="CheckCircle" size={24} className="text-green-600" />
                    </div>
                  </div>
                ))}
                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Info" size={20} className="text-blue-600" />
                    <span className="font-semibold text-blue-900">Tank Size</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">{demoData?.tankMixing?.tankSize} gallons</div>
                </div>
              </div>

              {/* Mixing Instructions */}
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="CheckCircle" size={24} className="text-green-600" />
                    <span className="font-semibold text-green-900">Compatibility: {demoData?.tankMixing?.compatibility}</span>
                  </div>
                  <p className="text-sm text-green-700">These products are safe to mix together</p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Mixing Order</h3>
                  <div className="space-y-2">
                    {demoData?.tankMixing?.mixingOrder?.map((step, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <span className="text-gray-900">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-yellow-900 mb-1">Safety Reminder</div>
                      <p className="text-sm text-yellow-700">Always wear appropriate PPE and follow label instructions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-8 bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-8 border-2 border-green-200">
          <div className="text-center max-w-2xl mx-auto">
            <Icon name="Calculator" size={48} className="text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Unlock All Premium Calculators</h3>
            <p className="text-gray-600 mb-6">
              Upgrade to access all advanced calculators, save your calculations, and get AI-powered IPM treatment plans tailored to your operation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white"
                onClick={() => navigate('/pricing-page')}
              >
                Start 14-Day Free Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50"
                onClick={() => navigate('/login')}
              >
                Sign Up Free
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">✓ All calculators included • ✓ Save calculations • ✓ AI treatment plans</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewCalculators;